import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus, FaTrash, FaChartLine, FaTable, FaTimes, FaUserCheck, FaMobileAlt, FaHospital } from 'react-icons/fa';
import CustomSpinner from '../../../Components/CommonComponents/CustomSpinner';
import ConfirmDialog from '../../../Components/CommonComponents/ConfirmDialog';
import VitalsDynamicForm from '../../../Components/CommonComponents/VitalsDynamicForm';
import VitalsCharts from './VitalsChart';
import exportToExcel from '../../../UtilityHookFunctions/exportToExcel';
import { FREQUENCIES } from '../Vitals/VitalTemplateBuilder';
import {
  fetchTemplates,
  fetchAssignments,
  assignTemplate,
  updateAssignment,
  unassignTemplate,
  fetchRecords,
  createRecord,
  deleteRecord,
  fetchSeries,
} from '../../../Services/patientProfile';

const nowLocalInput = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const fmtDateTime = (value) =>
  new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Assign a clinic template to this patient. */
const AssignPanel = ({ templates, assigned, onAssign, onClose }) => {
  const available = templates.filter((t) => t.active && !assigned.includes(String(t._id)));
  const [templateId, setTemplateId] = useState('');
  const [frequency, setFrequency] = useState('');
  const [instructions, setInstructions] = useState('');
  const [saving, setSaving] = useState(false);

  const chosen = templates.find((t) => String(t._id) === templateId);

  const submit = async () => {
    if (!templateId) return toast.error('Choose a template');
    setSaving(true);
    try {
      await onAssign({ templateId, frequency: frequency || chosen?.frequency, instructions });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-primary-200 bg-primary-50/50 p-5">
      <div className="flex items-start justify-between">
        <h4 className="font-semibold text-slate-800">Assign a vitals template</h4>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
          <FaTimes />
        </button>
      </div>

      {!available.length ? (
        <p className="mt-3 text-sm text-slate-600">
          Every active template is already assigned.{' '}
          <Link to="/vitals-templates" className="font-medium text-primary-600 hover:underline">
            Build a new one
          </Link>
          .
        </p>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Template</label>
              <select
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
              >
                <option value="">Select…</option>
                {available.map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.name} ({t.fields?.length} fields)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                How often
              </label>
              <select
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={frequency || chosen?.frequency || ''}
                onChange={(e) => setFrequency(e.target.value)}
              >
                {FREQUENCIES.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Instructions for the patient (optional)
              </label>
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={instructions}
                placeholder="e.g. Record every morning before breakfast"
                onChange={(e) => setInstructions(e.target.value)}
              />
            </div>
          </div>

          <button
            onClick={submit}
            disabled={saving}
            className="mt-4 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-60"
          >
            {saving ? 'Assigning…' : 'Assign template'}
          </button>
        </>
      )}
    </div>
  );
};

export default function VitalsTab({ patientId }) {
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState(null);
  const [activeTemplateId, setActiveTemplateId] = useState('');
  const [series, setSeries] = useState(null);
  const [records, setRecords] = useState([]);
  const [view, setView] = useState('graph');
  const [showAssign, setShowAssign] = useState(false);

  const [entryOpen, setEntryOpen] = useState(false);
  const [entryValues, setEntryValues] = useState({});
  const [entryAt, setEntryAt] = useState(nowLocalInput());
  const [entryNotes, setEntryNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const [pendingUnassign, setPendingUnassign] = useState(null);
  const [pendingDeleteRecord, setPendingDeleteRecord] = useState(null);

  const activeAssignment = useMemo(
    () => (assignments || []).find((a) => String(a.templateId) === String(activeTemplateId)),
    [assignments, activeTemplateId]
  );
  const activeTemplate = activeAssignment?.template;

  const loadAssignments = useCallback(async () => {
    try {
      const list = await fetchAssignments(patientId);
      setAssignments(list);
      setActiveTemplateId((current) => {
        if (current && list.some((a) => String(a.templateId) === String(current))) return current;
        return list.length ? String(list[0].templateId) : '';
      });
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not load assigned templates');
      setAssignments([]);
    }
  }, [patientId]);

  useEffect(() => {
    loadAssignments();
    fetchTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, [loadAssignments]);

  const loadData = useCallback(async () => {
    if (!activeTemplateId) {
      setSeries(null);
      setRecords([]);
      return;
    }
    try {
      const [s, r] = await Promise.all([
        fetchSeries(patientId, { templateId: activeTemplateId }),
        fetchRecords(patientId, { templateId: activeTemplateId, limit: 200 }),
      ]);
      setSeries(s);
      setRecords(r?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not load readings');
    }
  }, [patientId, activeTemplateId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAssign = async (payload) => {
    try {
      await assignTemplate(patientId, payload);
      toast.success('Template assigned');
      await loadAssignments();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not assign the template');
      throw error;
    }
  };

  const changeFrequency = async (assignment, frequency) => {
    try {
      await updateAssignment(assignment._id, { frequency });
      setAssignments((list) => list.map((a) => (a._id === assignment._id ? { ...a, frequency } : a)));
      toast.success('Frequency updated');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not update the frequency');
    }
  };

  const confirmUnassign = async () => {
    try {
      await unassignTemplate(pendingUnassign._id);
      toast.success('Template unassigned');
      setPendingUnassign(null);
      await loadAssignments();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not unassign the template');
    }
  };

  const openEntry = () => {
    setEntryValues({});
    setEntryAt(nowLocalInput());
    setEntryNotes('');
    setEntryOpen(true);
  };

  const saveEntry = async () => {
    setSaving(true);
    try {
      await createRecord(patientId, {
        templateId: activeTemplateId,
        recordedAt: new Date(entryAt).toISOString(),
        values: entryValues,
        notes: entryNotes,
      });
      toast.success('Reading saved');
      setEntryOpen(false);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not save the reading');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteRecord = async () => {
    try {
      await deleteRecord(pendingDeleteRecord._id);
      toast.success('Reading deleted');
      setPendingDeleteRecord(null);
      await loadData();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not delete the reading');
    }
  };

  if (assignments === null) return <CustomSpinner />;

  return (
    <div className="space-y-5">
      {/* --------------------------- assigned templates --------------------------- */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold text-slate-800">Assigned vitals templates</h3>
            <p className="text-xs text-slate-500">
              The forms this patient records against, at the clinic or from their own link.
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/vitals-templates"
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Manage templates
            </Link>
            <button
              onClick={() => setShowAssign((s) => !s)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-600"
            >
              <FaPlus /> Assign
            </button>
          </div>
        </div>

        {showAssign && (
          <div className="mt-4">
            <AssignPanel
              templates={templates}
              assigned={assignments.map((a) => String(a.templateId))}
              onAssign={handleAssign}
              onClose={() => setShowAssign(false)}
            />
          </div>
        )}

        {!assignments.length ? (
          <p className="mt-4 rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
            No templates assigned yet. Assign one so this patient can start recording vitals.
          </p>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {assignments.map((a) => (
              <div
                key={a._id}
                className={`flex flex-col rounded-lg border p-3 transition ${
                  String(a.templateId) === String(activeTemplateId)
                    ? 'border-primary-400 bg-primary-50/50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <button
                  onClick={() => setActiveTemplateId(String(a.templateId))}
                  className="min-w-0 flex-1 text-left"
                >
                  <p className="truncate text-sm font-medium text-slate-800">{a.templateName}</p>
                  <p className="text-xs text-slate-500">{a.template?.fields?.length || 0} fields</p>
                  {a.instructions ? (
                    <p className="mt-1 line-clamp-2 text-xs text-slate-500">{a.instructions}</p>
                  ) : null}
                </button>

                <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-2">
                  <select
                    value={a.frequency}
                    onChange={(e) => changeFrequency(a, e.target.value)}
                    className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2 py-1 text-xs"
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={() => setPendingUnassign(a)}
                    title="Unassign"
                    className="shrink-0 rounded p-1.5 text-rose-400 hover:bg-rose-50 hover:text-rose-600"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ------------------------------- readings ------------------------------- */}
      {activeTemplate && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-slate-800">{activeTemplate.name}</h3>
              <p className="text-xs text-slate-500">
                {series?.totalRecords || 0} reading{series?.totalRecords === 1 ? '' : 's'}
                {series?.lastRecordedAt ? ` · last on ${fmtDateTime(series.lastRecordedAt)}` : ''}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-slate-300 p-0.5">
                <button
                  onClick={() => setView('graph')}
                  className={`inline-flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium ${
                    view === 'graph' ? 'bg-primary-500 text-white' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <FaChartLine /> Graphs
                </button>
                <button
                  onClick={() => setView('table')}
                  className={`inline-flex items-center gap-1.5 rounded px-3 py-1 text-xs font-medium ${
                    view === 'table' ? 'bg-primary-500 text-white' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <FaTable /> Table
                </button>
              </div>

              <button
                type="button"
                disabled={!records.length}
                onClick={() =>
                  exportToExcel(
                    records,
                    `Vitals_${activeTemplate.name || 'readings'}`,
                    [
                      { key: 'recordedAt', label: 'Recorded', format: (v) => fmtDateTime(v) },
                      { key: 'source', label: 'Source', format: (v) => (v === 'portal' ? 'Patient' : 'Clinic') },
                      ...(activeTemplate.fields || []).map((f) => ({
                        key: 'values',
                        label: `${f.label}${f.unit ? ` (${f.unit})` : ''}`,
                        format: (vals) => {
                          const v = vals?.[f.key];
                          if (v === true) return 'Yes';
                          if (v === false) return 'No';
                          if (v && typeof v === 'object') return JSON.stringify(v);
                          return v ?? '';
                        },
                      })),
                      { key: 'notes', label: 'Notes' },
                    ],
                    'Vitals'
                  )
                }
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Export to Excel
              </button>

              <button
                onClick={openEntry}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
              >
                <FaPlus /> Record reading
              </button>
            </div>
          </div>

          {entryOpen && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/40 p-5">
              <div className="flex items-start justify-between">
                <h4 className="font-semibold text-slate-800">Record a reading at the clinic</h4>
                <button onClick={() => setEntryOpen(false)} className="text-slate-400 hover:text-slate-700">
                  <FaTimes />
                </button>
              </div>

              <div className="mt-3 max-w-xs">
                <label className="block text-sm font-medium text-slate-700">Date &amp; time</label>
                <input
                  type="datetime-local"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={entryAt}
                  onChange={(e) => setEntryAt(e.target.value)}
                />
              </div>

              <div className="mt-4">
                <VitalsDynamicForm template={activeTemplate} values={entryValues} onChange={setEntryValues} />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700">Notes (optional)</label>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={entryNotes}
                  onChange={(e) => setEntryNotes(e.target.value)}
                />
              </div>

              <button
                onClick={saveEntry}
                disabled={saving}
                className="mt-4 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save reading'}
              </button>
            </div>
          )}

          <div className="mt-5">
            {view === 'graph' ? (
              <VitalsCharts series={series?.series || []} />
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Recorded</th>
                      <th className="px-3 py-2 font-semibold">Source</th>
                      {(activeTemplate.fields || []).map((f) => (
                        <th key={f.key} className="px-3 py-2 font-semibold">
                          {f.label}
                          {f.unit ? ` (${f.unit})` : ''}
                        </th>
                      ))}
                      <th className="px-3 py-2 font-semibold">Notes</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {records.map((r) => (
                      <tr key={r._id} className="hover:bg-slate-50">
                        <td className="whitespace-nowrap px-3 py-2 text-slate-700">{fmtDateTime(r.recordedAt)}</td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              r.source === 'portal'
                                ? 'bg-violet-100 text-violet-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {r.source === 'portal' ? <FaMobileAlt /> : <FaHospital />}
                            {r.source === 'portal' ? 'Patient' : 'Clinic'}
                          </span>
                        </td>
                        {(activeTemplate.fields || []).map((f) => (
                          <td key={f.key} className="whitespace-nowrap px-3 py-2 text-slate-800">
                            {r.values?.[f.key] === true
                              ? 'Yes'
                              : r.values?.[f.key] === false
                              ? 'No'
                              : r.values?.[f.key] ?? '—'}
                          </td>
                        ))}
                        <td className="px-3 py-2 text-slate-500">{r.notes || '—'}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setPendingDeleteRecord(r)}
                            className="rounded p-1.5 text-rose-400 hover:bg-rose-50 hover:text-rose-600"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!records.length && (
                      <tr>
                        <td
                          colSpan={(activeTemplate.fields?.length || 0) + 4}
                          className="px-3 py-8 text-center text-slate-500"
                        >
                          No readings recorded yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {view === 'graph' && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-500">
              <FaUserCheck /> Trend graphs are visible to clinic staff only — the patient's own link shows their
              recorded values without charts.
            </p>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!pendingUnassign}
        title="Unassign this template?"
        message={
          pendingUnassign
            ? `${pendingUnassign.templateName} will stop appearing on the patient's link. Readings already recorded are kept.`
            : ''
        }
        confirmLabel="Unassign"
        onConfirm={confirmUnassign}
        onCancel={() => setPendingUnassign(null)}
      />

      <ConfirmDialog
        isOpen={!!pendingDeleteRecord}
        title="Delete this reading?"
        message={
          pendingDeleteRecord
            ? `The reading from ${fmtDateTime(pendingDeleteRecord.recordedAt)} will be permanently removed.`
            : ''
        }
        onConfirm={confirmDeleteRecord}
        onCancel={() => setPendingDeleteRecord(null)}
      />
    </div>
  );
}
